import { createContext, useCallback, useContext } from 'react';
import {
  AssistantApi,
  AssistantAuditOperationListApi,
  AssistantCatalogApi,
  AssistantCreateApi,
  AssistantFrameApi,
  AssistantInstructionApi,
  AssistantInstructionCreateApi,
  AssistantInstructionListApi,
  AssistantInstructionUpdateApi,
  AssistantListApi,
  AssistantMarkerApi,
  AssistantNotificationApi,
  AssistantOptionsApi,
  AssistantSwarmApi,
  AssistantUpdateApi,
} from './AgentApiProviderTypes';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import { PromptApi } from './PromptApiProviderTypes';
import { AuditOperation } from './TaskScheduleApiProviderTypes';
import { buildUrl } from '../utils/buildQuery';
import { normalizeDate } from '../utils/formatDate';

interface AgentApiContextType {
  getAgents: (skip?: number, limit?: number) => Promise<AssistantListApi>;
  getAgentById: (agentId: string) => Promise<AssistantApi>;
  getAgentNotifications: (agentId: string) => Promise<AssistantNotificationApi[]>;
  getAgentCatalog: (agentId: string) => Promise<AssistantCatalogApi>;
  getAgentFrames: (agentId: string) => Promise<AssistantFrameApi[]>;
  getAgentMarkers: (agentId: string) => Promise<AssistantMarkerApi[]>;
  createAgent: (agent: AssistantCreateApi) => Promise<AssistantApi>;
  updateAgentById: (agentId: string, agent: AssistantUpdateApi) => Promise<AssistantApi>;
  deleteAgent: (agentId: string) => Promise<void>;
  getDictionaries: () => Promise<any[]>;
  getNotifications: () => Promise<AssistantNotificationApi[]>;
  getPrompts: () => Promise<PromptApi[]>;
  getFrames: () => Promise<AssistantFrameApi[]>;
  getMarkers: () => Promise<AssistantMarkerApi[]>;
  getCatalogs: () => Promise<any[]>;
  getAssistantOptions: () => Promise<AssistantOptionsApi>;
  //executeAgent: (agentId: string, messages: any[]) => Promise<any[]>;
  getAgentInstructions: (
    agentId: string,
    skip?: number,
    limit?: number,
  ) => Promise<AssistantInstructionListApi>;
  createAgentInstruction: (
    agentId: string,
    instruction: AssistantInstructionCreateApi,
  ) => Promise<AssistantInstructionApi>;
  updateAgentInstruction: (
    agentId: string,
    instructionID: string,
    instruction: AssistantInstructionUpdateApi,
  ) => Promise<AssistantInstructionApi>;
  deleteAgentInstruction: (agentId: string, instructionID: string) => Promise<void>;
  getRelevantInstruction: (agentId: string, search?: string, limit?: number) => Promise<any>;
  getAgentAuditOperations: (
    agentId: string,
    skip?: number,
    limit?: number,
    start_date?: string,
    end_date?: string,
    operationId?: string | null,
    threadId?: string | null,
  ) => Promise<AssistantAuditOperationListApi>;
  getAgentAuditOperationById: (agentId: string, auditId: string) => Promise<AuditOperation>;
  getAllSwarm: (skip?: number, limit?: number) => Promise<AssistantSwarmApi[]>;
  getAgentSwarm: (agentId: string) => Promise<AssistantApi[]>;
  getDocumentCollections: () => Promise<any[]>;
  getInformationPackageCollections: () => Promise<any[]>;
  getConnections: () => Promise<any[]>;
}

const AgentApiContext = createContext<AgentApiContextType | null>(null);

export function AgentApiProvider({ children }) {
  const { get, post, put, del } = useApiService(CONFIG.SERVER_ASSISTANT);

  const organizationApi = useApiService(CONFIG.SERVER_ORGANIZATION);

  const documentApi = useApiService(CONFIG.SERVER_DOCUMENT);

  const getAgents = useCallback(
    async (skip = 0, limit = 10): Promise<AssistantListApi> => {
      try {
        const response = await get(`/v1/assistants?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching agents:', error);
        throw error;
      }
    },
    [get],
  );

  const getAgentById = useCallback(
    async (agentId: string): Promise<AssistantApi> => {
      try {
        const response = await get(`/v1/assistants/${agentId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching agent by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getAgentNotifications = useCallback(
    async (agentId: string): Promise<AssistantNotificationApi[]> => {
      try {
        const response = await get(`/v1/assistants/${agentId}/notifications`);
        return response.data;
      } catch (error) {
        console.error('Error fetching agent notifications:', error);
        throw error;
      }
    },
    [get],
  );

  const getAgentCatalog = useCallback(
    async (agentId: string): Promise<AssistantCatalogApi> => {
      try {
        const response = await get(`/v1/assistants/${agentId}/catalog`);
        return response.data;
      } catch (error) {
        console.error('Error fetching agent catalog:', error);
        throw error;
      }
    },
    [get],
  );

  const getAgentFrames = useCallback(
    async (agentId: string): Promise<AssistantFrameApi[]> => {
      try {
        const response = await get(`/v1/assistants/${agentId}/assistant_frames`);
        return response.data;
      } catch (error) {
        console.error('Error fetching agent frames:', error);
        throw error;
      }
    },
    [get],
  );

  const createAgent = useCallback(
    async (agent: AssistantCreateApi): Promise<AssistantApi> => {
      try {
        const response = await put(`/v1/assistants`, agent);
        return response.data;
      } catch (error) {
        console.error('Error creating agent:', error);
        throw error;
      }
    },
    [put],
  );

  const updateAgentById = useCallback(
    async (agentId: string, agent: AssistantUpdateApi): Promise<AssistantApi> => {
      try {
        const response = await put(`/v1/assistants/${agentId}`, agent);
        return response.data;
      } catch (error) {
        console.error('Error updating agent:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteAgent = useCallback(
    async (agentId: string): Promise<void> => {
      try {
        const response = await del(`/v1/assistants/${agentId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting agent:', error);
        throw error;
      }
    },
    [del],
  );

  const getNotifications = useCallback(async (): Promise<AssistantNotificationApi[]> => {
    try {
      const response = await get(`/v1/notifications`);
      return response.data.items ?? [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }, [get]);

  const getPrompts = useCallback(async (): Promise<PromptApi[]> => {
    try {
      const response = await get(`/v1/prompts`);
      return response.data.items;
    } catch (error) {
      console.error('Error fetching prompts:', error);
      throw error;
    }
  }, [get]);

  const getFrames = useCallback(async (): Promise<AssistantFrameApi[]> => {
    try {
      const response = await get(`/v1/assistant_frames?limit=500`);
      return response.data.items;
    } catch (error) {
      console.error('Error fetching frames:', error);
      throw error;
    }
  }, [get]);

  const getMarkers = useCallback(async (): Promise<AssistantMarkerApi[]> => {
    try {
      const response = await get(`/v1/markers?limit=500`);
      return response.data.items;
    } catch (error) {
      console.error('Error fetching markers:', error);
      throw error;
    }
  }, [get]);

  const getAgentMarkers = useCallback(
    async (agentId: string): Promise<AssistantMarkerApi[]> => {
      try {
        const response = await get(`/v1/assistants/${agentId}/assistant_markers`);
        return response.data;
      } catch (error) {
        console.error('Error fetching agent markers:', error);
        throw error;
      }
    },
    [get],
  );

  //Add types for catalogs and dictionaries in the future when those APIs are implemented
  const getCatalogs = useCallback(async () => {
    try {
      const response = await documentApi.get(`/v1/e_commerce/catalogs`);
      return response.data.items ?? [];
    } catch (error) {
      console.error('Error fetching product catalogs:', error);
      throw error;
    }
  }, [documentApi]);

  const getDictionaries = useCallback(async () => {
    try {
      const response = await get(`/v1/taxonomy_dictionary`);
      return response.data.items ?? [];
    } catch (error) {
      console.error('Error fetching dictionaries:', error);
      throw error;
    }
  }, [get]);

  // const executeAgent = useCallback(
  //   async (agentId, messages) => {
  //     try {
  //       const payload = messages.map((item) => {
  //         if (item.from === 'user') {
  //           return { human: item.rawText };
  //         } else if (item.from === 'assistant') {
  //           return { ai: item.rawText };
  //         }
  //       });
  //       console.log('Payload to Assistant:', payload);
  //       console.log('Data to Assistant:', messages);
  //       const response = await post(`/v1/assistants/${agentId}/streams`, { thread: payload });
  //       const responseThread = response.data.thread.map((item) => {
  //         if (item.ai) {
  //           return {
  //             from: 'assistant',
  //             rawText: item.ai,
  //             time: new Date().toTimeString().slice(0, 8),
  //             metadata: item.metadata,
  //           };
  //         } else if (item.human) {
  //           return {
  //             from: 'user',
  //             rawText: item.human,
  //             time: new Date().toTimeString().slice(0, 8),
  //           };
  //         }
  //       });
  //       return responseThread;
  //     } catch (error) {
  //       console.error('Error executing agent:', error);
  //       throw error;
  //     }
  //   },
  //   [post],
  // );

  const getAgentInstructions = useCallback(
    async (
      agentId: string,
      skip: number = 0,
      limit: number,
    ): Promise<AssistantInstructionListApi> => {
      try {
        const response = await get(
          `/v1/assistants/${agentId}/instructions?skip=${skip}&limit=${limit}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching agent instructions', error);
        throw error;
      }
    },
    [get],
  );

  const getAgentAuditOperations = useCallback(
    async (
      agentId: string,
      skip: number = 0,
      limit: number = 10,
      start_date: string,
      end_date: string,
      operationId: string | null = null,
      threadId: string | null = null,
    ): Promise<AssistantAuditOperationListApi> => {
      try {
        const url = buildUrl(`/v1/assistants/${agentId}/audit_operations`, {
          skip,
          limit,
          start_date: normalizeDate(start_date),
          end_date: normalizeDate(end_date),
          operation_id: operationId,
          thread_id: operationId ? null : threadId,
        });

        const response = await get(url);
        return response.data;
      } catch (error) {
        console.error('Error fetching agent audit operation:', error);
        throw error;
      }
    },
    [get],
  );

  const getAgentAuditOperationById = useCallback(
    async (agentId: string, auditId: string): Promise<AuditOperation> => {
      try {
        const response = await get(`/v1/assistants/${agentId}/audit_operations/${auditId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching agent audit operation:', error);
        throw error;
      }
    },
    [get],
  );

  const createAgentInstruction = useCallback(
    async (
      agentId: string,
      instruction: AssistantInstructionCreateApi,
    ): Promise<AssistantInstructionApi> => {
      try {
        const response = await post(`/v1/assistants/${agentId}/instructions`, instruction);
        return response.data;
      } catch (error) {
        console.error('Error creating agent instruction:', error);
        throw error;
      }
    },
    [post],
  );

  const updateAgentInstruction = useCallback(
    async (
      agentId: string,
      instructionID: string,
      instruction: AssistantInstructionUpdateApi,
    ): Promise<AssistantInstructionApi> => {
      try {
        const response = await put(
          `/v1/assistants/${agentId}/instructions/${instructionID}`,
          instruction,
        );
        return response.data;
      } catch (error) {
        console.error('Error updating agent instruction:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteAgentInstruction = useCallback(
    async (agentId: string, instructionID: string): Promise<void> => {
      try {
        const response = await del(`/v1/assistants/${agentId}/instructions/${instructionID}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting agent instruction:', error);
        throw error;
      }
    },
    [del],
  );

  const getAssistantOptions = useCallback(async (): Promise<AssistantOptionsApi> => {
    try {
      const response = await get(`/v1/assistants/options`);
      return response.data;
    } catch (error) {
      console.error('Error fetching agent model options:', error);
      throw error;
    }
  }, [get]);

  //Add types for relevant instruction response when the API is implemented
  const getRelevantInstruction = useCallback(
    async (agentId: string, search: string = '', limit: number = 10) => {
      try {
        const response = await get(
          `/v1/assistants/${agentId}/instructions/find_relevant_instruction?search=${search}&limit=${limit}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching agent instructions', error);
        throw error;
      }
    },
    [get],
  );

  const getAllSwarm = useCallback(
    async (skip: number = 0, limit: number = 10): Promise<AssistantSwarmApi[]> => {
      try {
        const response = await get(`/v1/assistant_swarms?skip=${skip}&limit=${limit}`);
        return response.data.items;
      } catch (error) {
        console.error('Error fetching swarm agents:', error);
        throw error;
      }
    },
    [get],
  );

  const getAgentSwarm = useCallback(
    async (agentId: string): Promise<AssistantApi[]> => {
      try {
        const response = await get(`/v1/assistants/${agentId}/swarms`);
        return response.data;
      } catch (error) {
        console.error('Error fetching swarm agents:', error);
        throw error;
      }
    },
    [get],
  );
  //add types for document collection when the API is implemented
  const getDocumentCollections = useCallback(async () => {
    try {
      const response = await documentApi.get(`/v1/document_collection`);
      return response.data.items ?? [];
    } catch (error) {
      console.error('Error fetching document collections:', error);
      throw error;
    }
  }, [documentApi]);

  const getInformationPackageCollections = useCallback(async () => {
    try {
      const response = await documentApi.get(`/v1/information_package_collection`);
      return response.data.items ?? [];
    } catch (error) {
      console.error('Error fetching information package collections:', error);
      throw error;
    }
  }, [documentApi]);

  const getConnections = useCallback(async () => {
    try {
      const response = await organizationApi.get(`/v1/connections`);
      return response.data.items ?? [];
    } catch (error) {
      console.error('Error fetching connections:', error);
      throw error;
    }
  }, [get]);

  return (
    <AgentApiContext.Provider
      value={{
        getAgents,
        getAgentById,
        getAgentNotifications,
        getAgentCatalog,
        getAgentFrames,
        getAgentMarkers,
        createAgent,
        updateAgentById,
        deleteAgent,
        getNotifications,
        getPrompts,
        getFrames,
        getMarkers,
        getCatalogs,
        //executeAgent,
        getAgentInstructions,
        createAgentInstruction,
        updateAgentInstruction,
        deleteAgentInstruction,
        getDictionaries,
        getRelevantInstruction,
        getAgentAuditOperations,
        getAllSwarm,
        getAgentSwarm,
        getDocumentCollections,
        getInformationPackageCollections,
        getConnections,
        getAgentAuditOperationById,
        getAssistantOptions,
      }}
    >
      {children}
    </AgentApiContext.Provider>
  );
}

export function useAgentApi(): AgentApiContextType {
  const context = useContext(AgentApiContext);
  if (!context) {
    throw new Error('useAgentApi must be used within a AgentApiContext');
  }
  return context;
}
